import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API } from 'src/app/shared/models/common';
import { environment } from 'src/environments/environment';

/**
 * Scope for the action-board counts, taken from the dashboard's top filters.
 * 0 means "all" on every field — the endpoints expect the key to be present
 * with 0 rather than omitted.
 *
 * Note the rename: the top-filter form calls it `loanOfficerId`, these
 * endpoints call it `fieldOfficerId`.
 */
export interface ActionBoardScope {
  areaId: number;
  branchId: number;
  fieldOfficerId: number;
  /** Inclusive range from the top filters, already formatted yyyy-MM-dd. */
  startDate: string;
  endDate: string;
}

/**
 * Counts behind the dashboard's "Action Needed!" board.
 *
 * Every endpoint returns the count directly in `data` (i.e. API<number>,
 * `{ code, message, successful, data: 578 }`). The scoped endpoints also honour
 * the top filters' start/end date range.
 *
 * The board is a passive widget on an already-busy dashboard, so each request
 * carries `suppress-error-toast`: a failing count must not pop a toast (five
 * endpoints could otherwise stack five of them on load). Callers surface the
 * failure on the card instead.
 */
@Injectable({
  providedIn: 'root',
})
export class ActionBoardService {
  private readonly options = {
    headers: { 'suppress-error-toast': 'true' },
  };

  constructor(private http: HttpClient) {}

  /** accounts service: scopes by area, branch, field officer and date range. */
  private accountScope(scope: ActionBoardScope): HttpParams {
    return new HttpParams()
      .set('areaId', scope.areaId)
      .set('branchId', scope.branchId)
      .set('fieldOfficerId', scope.fieldOfficerId)
      .set('startDate', scope.startDate)
      .set('endDate', scope.endDate);
  }

  /** core service: the clients count scopes by area, branch and date range. */
  private clientScope(scope: ActionBoardScope): HttpParams {
    return new HttpParams()
      .set('areaId', scope.areaId)
      .set('branchId', scope.branchId)
      .set('startDate', scope.startDate)
      .set('endDate', scope.endDate);
  }

  public getPendingClientsCount(
    scope: ActionBoardScope,
  ): Observable<API<number>> {
    return this.http.get<API<number>>(
      `${environment.baseUrl}core/clients/pending-count`,
      { ...this.options, params: this.clientScope(scope) },
    );
  }

  public getPendingLoanApplicationsCount(
    scope: ActionBoardScope,
  ): Observable<API<number>> {
    return this.http.get<API<number>>(
      `${environment.baseUrl}accounts/loan-accounts/pending-applications-count`,
      { ...this.options, params: this.accountScope(scope) },
    );
  }

  public getPendingDisbursementsCount(
    scope: ActionBoardScope,
  ): Observable<API<number>> {
    return this.http.get<API<number>>(
      `${environment.baseUrl}accounts/loan-accounts/pending-disbursements-count`,
      { ...this.options, params: this.accountScope(scope) },
    );
  }

  public getPendingDepositAccountsCount(
    scope: ActionBoardScope,
  ): Observable<API<number>> {
    return this.http.get<API<number>>(
      `${environment.baseUrl}accounts/saving-accounts/pending-count`,
      { ...this.options, params: this.accountScope(scope) },
    );
  }

  /** Support tickets are org-wide — no scope params. */
  public getOpenSupportTicketsCount(): Observable<API<number>> {
    return this.http.get<API<number>>(
      `${environment.baseUrl}core/tickets/open-count`,
      this.options,
    );
  }
}
